"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Sparkles, CircleCheck as CheckCircle, CircleAlert as AlertCircle } from 'lucide-react';
export default function GeoComparisonSlider() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const traditionalOpacity = Math.max(0, Math.min(1, (sliderPosition - 10) / 12));
  const aiOpacity = Math.max(0, Math.min(1, (90 - sliderPosition) / 12));
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: sliderRef,
    isVisible: sliderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const updateSliderPosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPosition(percent);
  }, []);
  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updateSliderPosition(e.clientX);
  };
  const handleContainerPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    handlePointerDown(e);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    updateSliderPosition(e.clientX);
  };
  const handlePointerUp = (e: React.PointerEvent) => {
    if (isDragging) {
      setIsDragging(false);
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 700);
    }
  };
  const inspirations = [{
    id: 1,
    label: "Traditional SEO Ranking"
  }, {
    id: 2,
    label: "AI-Driven Visibility"
  }, {
    id: 3,
    label: "Predictive Search Authority"
  }];
  useEffect(() => {
    const onUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), 700);
      }
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [isDragging]);
  return <section data-section-id="1422" id="geocomparisonslider" className="relative bg-background text-foreground py-24 md:py-44 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px pointer-events-none">
        <div className="absolute top-0 left-0 h-px w-1/2 bg-red-900" />
        <div className="absolute -top-[3px] h-[7px] w-[7px] rounded-lg bg-red-800" style={{
        left: "25%"
      }} />
      </div>

      <div className="absolute right-0 top-1/3 w-[700px] h-[600px] pointer-events-none opacity-70 bg-gradient-to-br from-red-900/20 to-transparent" />

      <div className="relative max-w-[1440px] mx-auto px-5 md:px-8 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={sliderRef} className="lg:col-span-8 order-1 transition-all duration-700 ease-out" style={{
          opacity: sliderVisible ? 1 : 0,
          transform: sliderVisible ? "translateY(0)" : "translateY(24px)"
        }}>
            {/* SaaS Browser Frame Container */}
            <div ref={containerRef} className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden select-none touch-none rounded-xl border border-white/10 bg-[#0B0B0C] shadow-2xl flex flex-col" onPointerMove={handlePointerMove} onPointerDown={handleContainerPointerDown}>
              {/* Top Browser Bar */}
              <div className="h-9 md:h-11 bg-[#111112] border-b border-white/10 flex items-center px-3 md:px-4 justify-between shrink-0 select-none z-20">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-lg bg-[#FF5F56] opacity-80" />
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-lg bg-[#FFBD2E] opacity-80" />
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-lg bg-[#27C93F] opacity-80" />
                </div>
                <div className="px-3 md:px-5 py-1 bg-white/5 border border-white/10 rounded-lg text-[10px] md:text-xs text-white/60 font-mono flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-lg bg-emerald-500/80 animate-pulse" />
                  chat.openai.com
                </div>
                <div className="w-12 md:w-16" />
              </div>

              {/* Reveal Zone */}
              <div className="relative flex-1 overflow-hidden">
                {/* Labels Header */}
                <div className="absolute top-3 left-4 md:left-6 z-30 pointer-events-none transition-opacity duration-200" style={{
                opacity: traditionalOpacity
              }}>
                  <span className="font-mono text-[10px] md:text-xs uppercase tracking-wider text-white/55 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-lg border border-white/10">
                    TRADITIONAL
                  </span>
                </div>
                <div className="absolute top-3 right-4 md:right-6 z-30 pointer-events-none transition-opacity duration-200" style={{
                opacity: aiOpacity
              }}>
                  <span className="font-mono text-[10px] md:text-xs uppercase tracking-wider text-emerald-400 bg-emerald-950/60 backdrop-blur-sm px-2.5 py-1 rounded-lg border border-emerald-500/30 font-medium">
                    AI-OPTIMIZED
                  </span>
                </div>

                {/* AFTER Base Layer */}
                <div className="absolute inset-0 bg-[#0E1110] p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col justify-between text-left overflow-y-auto">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-950/30 via-transparent to-transparent pointer-events-none" />

                  <div className="relative z-10 space-y-4 md:space-y-5">
                    {/* User Question */}
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white/10 flex items-center justify-center text-xs text-white font-medium shrink-0">
                        You
                      </div>
                      <div className="bg-white/5 border border-white/10 px-3.5 py-2 rounded-lg text-xs md:text-sm text-gray-200">
                        Who's the best plumber near me?
                      </div>
                    </div>

                    {/* ChatGPT Response */}
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="flex-1 space-y-3">
                        <p className="text-xs md:text-sm text-gray-200 font-medium">
                          <span className="text-emerald-400 font-semibold">Your Business</span> is the top-rated plumber in your area — here's why:
                        </p>

                        {/* Highlighted Premium Card */}
                        <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-lg p-3.5 md:p-4 shadow-lg relative overflow-hidden">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-sm md:text-base text-white">Your Business</h4>
                                <span className="inline-flex items-center gap-1 text-[10px] md:text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-lg font-medium">
                                  <CheckCircle className="w-3 h-3 text-emerald-400" /> Top AI Recommendation
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-amber-400 text-xs font-semibold">4.9 </span>
                                <span className="text-[11px] text-gray-400">(148 verified reviews)</span>
                              </div>
                            </div>
                          </div>

                          <p className="text-xs text-gray-300 mb-3 leading-relaxed">
                            Consistently recommended for rapid response times, transparent pricing, and top satisfaction ratings.
                          </p>

                          <div className="flex flex-wrap gap-1.5">
                            {['Top AI Recommendation', 'Verified Business', 'Licensed & Insured', 'Trusted Local Authority'].map((chip, idx) => <span key={idx} className="text-[10px] bg-white/5 border border-white/10 text-gray-300 px-2 py-0.5 rounded-lg">
                                {chip}
                              </span>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BEFORE Layer (Clipped Overlay) */}
                <div className="absolute inset-0 bg-[#0A0A0B] p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col justify-between text-left overflow-y-auto z-10" style={{
                clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
              }}>
                  <div className="space-y-4 md:space-y-5">
                    {/* User Question */}
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white/10 flex items-center justify-center text-xs text-white font-medium shrink-0">
                        You
                      </div>
                      <div className="bg-white/5 border border-white/10 px-3.5 py-2 rounded-lg text-xs md:text-sm text-gray-400">
                        Who's the best plumber near me?
                      </div>
                    </div>

                    {/* Generic ChatGPT Response */}
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white/10 flex items-center justify-center text-gray-400 shrink-0">
                        <Sparkles className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 space-y-3">
                        <p className="text-xs md:text-sm text-gray-400">Here are search results found online:</p>

                        <div className="space-y-2">
                          {[{
                          domain: 'citywide-plumbing.com',
                          desc: 'Directory listing with unverified status.'
                        }, {
                          domain: 'metroplumbers.net',
                          desc: 'Standard business record, missing data.'
                        }, {
                          domain: 'quickfix-plumbing.co',
                          desc: 'No structured local citations found.'
                        }].map((item, idx) => <div key={idx} className="p-2.5 bg-white/[0.02] border border-white/5 rounded-lg flex items-center justify-between">
                              <div>
                                <span className="text-xs text-gray-400 font-mono block">{item.domain}</span>
                                <span className="text-[11px] text-gray-500 block">{item.desc}</span>
                              </div>
                              <span className="text-[10px] text-gray-600 uppercase font-mono">Sponsored</span>
                            </div>)}
                        </div>

                        <div className="mt-3 p-2 bg-red-950/20 border border-red-900/30 rounded-lg flex items-center gap-2 text-red-400/80 text-xs">
                          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                          <span>No verified local recommendation.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vertical Divider Line */}
                <div className="absolute top-0 bottom-0 w-px pointer-events-none bg-red-800 z-20" style={{
                left: `${sliderPosition}%`,
                transform: "translateX(-0.5px)"
              }} />

                {/* Interactive Drag Handle */}
                <button type="button" aria-label="Drag to compare" onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} className="absolute top-1/2 cursor-ew-resize touch-none z-30" style={{
                left: `${sliderPosition}%`,
                transform: "translate(-50%, -50%)",
                width: "36px",
                height: "36px",
                background: "transparent",
                border: "none",
                padding: 0
              }}>
                  <span className="absolute top-1/2 left-1/2 rounded-lg block border-2 border-white/80 flex items-center justify-center shadow-lg bg-destructive" style={{
                  width: "26px",
                  height: "26px",
                  transform: `translate(-50%, -50%) scale(${isPulsing || isDragging ? 1.25 : 1})`,
                  transition: isPulsing ? "transform 700ms cubic-bezier(.2,.8,.2,1)" : "transform 300ms ease-out",
                  boxShadow: isDragging ? "0 0 20px rgba(185, 28, 28, 0.8), 0 0 0 4px rgba(185, 28, 28, 0.3)" : "0 0 10px rgba(185, 28, 28, 0.4)"
                }}>
                    <div className="flex gap-0.5 items-center justify-center">
                      <div className="w-0.5 h-2.5 bg-white/90 rounded-lg" />
                      <div className="w-0.5 h-2.5 bg-white/90 rounded-lg" />
                    </div>
                  </span>
                </button>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <span className="font-default text-[11px] uppercase tracking-widest text-destructive">
                DRAG TO COMPARE
              </span>
              <div className="flex-1 h-px bg-gray-800" />
            </div>
          </div>

          <div ref={headingRef} className="lg:col-span-4 order-2 flex flex-col">
            <div className="transition-all duration-700 ease-out" style={{
            opacity: headingVisible ? 1 : 0,
            transform: headingVisible ? "translateY(0)" : "translateY(24px)"
          }}>
              <span className="font-default text-xs uppercase block mb-8 tracking-widest text-gray-400"></span>
            </div>

            <h2 className="font-default font-medium leading-[0.96] mb-8 transition-all duration-700 ease-out text-white" style={{
            fontSize: "clamp(2.5rem, 5.2vw, 5.5rem)",
            letterSpacing: "-0.03em",
            opacity: headingVisible ? 1 : 0,
            transform: headingVisible ? "translateY(0)" : "translateY(24px)",
            transitionDelay: "80ms"
          }}>
              Search,{" "}
              <span className="italic font-default font-normal inline-block text-destructive" style={{
              letterSpacing: "-0.01em",
              opacity: headingVisible ? 1 : 0,
              transition: "opacity 600ms ease-out 240ms"
            }}>Reimagined</span>
            </h2>

            <p className="font-default mb-10 transition-all duration-700 ease-out text-gray-300" style={{
            fontSize: "clamp(1.0625rem, 1.18vw, 1.25rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em",
            opacity: headingVisible ? 1 : 0,
            transform: headingVisible ? "translateY(0)" : "translateY(24px)",
            transitionDelay: "160ms"
          }}>
              Being found is only the first step. Today's customers are increasingly asking AI for recommendations instead of scrolling through search results. Our approach combines SEO with Generative Engine Optimization (GEO) so your business isn't just visible—it becomes the trusted answer when customers are ready to buy.
            </p>

            <div className="flex items-center gap-2 mb-8">
              <div className="h-px w-24 bg-destructive" />
              <div className="h-[6px] w-[6px] rounded-lg bg-destructive" />
            </div>

            <ul className="space-y-4">
              {inspirations.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3 transition-all duration-500 ease-out" style={{
              opacity: headingVisible ? 1 : 0,
              transform: headingVisible ? "translateX(0)" : "translateX(-12px)",
              transitionDelay: `${280 + i * 80}ms`
            }}>
                  <span className="text-[10px] mt-1 text-destructive">●</span>
                  <span className="font-default uppercase text-gray-400" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.08em",
                lineHeight: 1.3
              }}>
                    {item.label}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}